import { ICheckItemChecks, IChecklist, IChecklistItem, IChecklistSummaryRow } from "@mctypes/checklist.types";

/** Full list of possible check keys, in order */
export const FULL_CHECK_KEYS = [
  "check1","check2","check3","check4","check5",
  "check6","check7","check8","check9","check10",
  "check11","check12","check13","check14","check15",
] as const satisfies readonly (keyof ICheckItemChecks)[];
type CheckKey = typeof FULL_CHECK_KEYS[number];

function makeRecord<K extends PropertyKey, V>(
  keys: readonly K[],
  factory: (key: K) => V
): Record<K, V> {
  const entries = keys.map((k) => [k, factory(k)] as const);
  return Object.fromEntries(entries) as Record<K, V>;
}
/** Only take active check keys from checklist.checkTypes */
function getActiveCheckKeys(checklist: IChecklist): CheckKey[] {
  const ct = checklist.checkTypes as Partial<Record<CheckKey, { active: boolean }>>;
  return FULL_CHECK_KEYS.filter((k) => ct?.[k]?.active === true);
}
export function summarizeChecklistItems(
  items: readonly IChecklistItem[],
  checklist: IChecklist
): IChecklistSummaryRow[] {
  const activeKeys = getActiveCheckKeys(checklist);
  if (activeKeys.length === 0) return [];

  const filteredItems = checklist.activeItemTags?.length > 0 ? items.filter(item => item.itemTags?.some(tag => checklist.activeItemTags.includes(tag))) : items;

  const byKey = makeRecord<CheckKey, IChecklistSummaryRow>(activeKeys, (key) => ({
    checkTitle: checklist.checkTypes[key]?.name || key,
    accepted: 0,
    rejected: 0,
    notRelevant: 0,
    notFilled: 0,
    notActive: 0,
    totalAssessed: 0,
    totalEligible: 0,
    percentValue: 0,
    notDone: 0,
    percentText: "0 / 0",
  }));

  for (const item of filteredItems ?? []) {
    if (!item) continue;

    // Hela raden inaktiv? -> räkna endast notActive och gå vidare till nästa item
    if (item.inactive?.isChecked) {
      for (const key of activeKeys) {
        byKey[key].notActive += 1;
      }
      continue;
    }

    // Aktiv rad → räkna status (DISABLED exkluderas som tidigare)
    for (const key of activeKeys) {
      const row = byKey[key];
      const check = item.checks?.[key];

      if (!check || !check.status) {
        row.notFilled += 1;
        row.totalEligible += 1; // missing räknas som eligible
        continue;
      }

      switch (check.status) {
        case "YES":
          row.accepted += 1;
          row.totalAssessed += 1;
          row.totalEligible += 1;
          break;
        case "NO":
          row.rejected += 1;
          row.totalAssessed += 1;
          row.totalEligible += 1;
          break;
        case "N/A":
          row.notFilled += 1;
          row.totalEligible += 1; // N/A ingår i eligible
          break;
        case "IR":
          row.notRelevant += 1;
          row.totalEligible += 1; // IR ingår i eligible
          break;
        case "UAV":
          row.notDone += 1;
          row.totalEligible += 1; // UAV ingår i eligible
          break;
        case "DISABLED":
          // helt exkluderad: varken numerator eller denominator
          break;
      }
    }
  }

  // Prosent = (YES + N/A) / eligible (eligible exkluderar inaktiva och DISABLED)
  for (const key of activeKeys) {
    const r = byKey[key];
    const numerator = r.accepted + r.notRelevant + r.notDone;
    const denom = r.totalEligible;
    r.percentValue = denom > 0 ? numerator / denom : 0;
    r.percentText = `${numerator} / ${denom}`;
  }

  return activeKeys.map((k) => byKey[k]);
}
